import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Box, TextField, IconButton, Typography, Avatar, Paper, Stack, Alert } from '@mui/material';
import { Send as SendIcon, ArrowBack as ArrowBackIcon, Check as CheckIcon } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { firestore } from '../../config/firebase';
import { ChatMessage, ChatUser } from '../../types/chat';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { getUsernameById } from '../../services/users';

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = useParams();
  const locationState = (location.state || {}) as {
    otherUserName?: string;
    otherUserDisplayName?: string;
    source?: string;
    chatInfo?: Record<string, unknown>;
  };
  const {
    otherUserName,
    otherUserDisplayName: otherUserDisplayNameFromState,
    source,
  } = locationState;
  type EnhancedChatMessage = ChatMessage & {
    localId?: string;
    status?: 'pending' | 'failed' | 'sent';
    optimisticTimestamp?: Date;
    errorMessage?: string;
  };

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [otherUserDisplayName, setOtherUserDisplayName] = useState<string>('');
  const [pendingMessages, setPendingMessages] = useState<EnhancedChatMessage[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);

  const flatListRef = useRef<HTMLDivElement>(null);

  // Get current user from Redux
  const currentUser = useSelector((state: RootState) => state.user?.user);
  const currentUserId = currentUser?._id || currentUser?.id;

  // Get chat info from location state or params
  const chatInfo = locationState?.chatInfo as Record<string, unknown> | undefined;

  const otherUserAvatarUrl =
    chatInfo &&
    typeof (chatInfo as { profilePhoto?: { url?: unknown } }).profilePhoto?.url === 'string'
      ? ((chatInfo as { profilePhoto?: { url?: string } }).profilePhoto!.url as string)
      : undefined;

  // Fetch other user's name when component mounts
  useEffect(() => {
    const fetchOtherUserName = async () => {
      
        // If otherUserName is an object with id, fetch the name
      if (otherUserDisplayNameFromState) {
        setOtherUserDisplayName(otherUserDisplayNameFromState);
      }

      if (otherUserName) {
        try {
          const name = await getUsernameById(otherUserName);
          setOtherUserDisplayName(name || otherUserDisplayNameFromState || 'Unknown User');
        } catch (error) {
          console.error('Error fetching other user name:', error);
          setOtherUserDisplayName(otherUserDisplayNameFromState || 'Unknown User');
        }
      } else if (!otherUserDisplayNameFromState) {
        setOtherUserDisplayName('Unknown User');
      }
    };

    fetchOtherUserName();
  }, [otherUserName, otherUserDisplayNameFromState]);

  console.log('🔍 Chat Component Debug Info:', {
    chatId,
    currentUserId,
    currentUser,
    chatInfo,
    location: location.pathname,
    messagesCount: messages.length
  });

  useEffect(() => {

    if (!chatId || !currentUserId) {
      console.log('❌ Missing required data:', { chatId, currentUserId });
      return;
    }

    console.log('📡 Setting up Firestore listener for chat:', chatId);

    const unsubscribe = firestore
      .collection('Chats')
      .doc(chatId)
      .collection('messages')
      .orderBy('timestamp', 'asc')
      .onSnapshot((snapshot: any) => {
        console.log('📨 Firestore snapshot received:', {
          docCount: snapshot.docs.length,
          chatId,
          timestamp: new Date().toISOString()
        });

        const msgs = snapshot.docs.map((doc: any) => {
          const data = doc.data();
          console.log('📝 Processing message doc:', { docId: doc.id, data });
          return {
            id: doc.id,
            text: data.text || '',
            senderId: data.senderId || '',
            timestamp: data.timestamp,
          };
        });

        setMessages(msgs);

        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          if (flatListRef.current) {
            flatListRef.current.scrollTop = flatListRef.current.scrollHeight;
            console.log('📜 Scrolled to bottom');
          }
        }, 100);
      });

    console.log('🔌 Firestore listener set up, returning unsubscribe function');
    return unsubscribe;
  }, [chatId, currentUserId]);

  const sendMessage = useCallback(async () => {
    console.log('📤 sendMessage called:', { input, chatId, currentUserId });

    if (!input.trim() || !chatId || !currentUserId) {
      console.log('❌ Cannot send message - missing data:', {
        hasInput: !!input.trim(),
        chatId,
        currentUserId
      });
      return;
    }

    console.log('⏳ Starting to send message...');
    const trimmedInput = input.trim();
    const localId = `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimisticTimestamp = new Date();
    const optimisticMessage: EnhancedChatMessage = {
      id: localId,
      localId,
      text: trimmedInput,
      senderId: currentUserId,
      timestamp: optimisticTimestamp,
      optimisticTimestamp,
      status: 'pending',
    };

    setPendingMessages((prev) => [...prev, optimisticMessage]);
    setInput('');
    setSendError(null);

    try {
      const message = {
        text: trimmedInput,
        senderId: currentUserId,
        timestamp: firestore.FieldValue.serverTimestamp(),
      };

      console.log('📝 Message object created:', message);

      // Update chat room with last message
      console.log('🔄 Updating chat room...');
      await firestore
        .collection('Chats')
        .doc(chatId)
        .set({
          participants: [currentUserId, otherUserName], // Keep original participants structure
          lastMessage: trimmedInput,
          lastMessageAt: firestore.FieldValue.serverTimestamp(),
          createdAt: firestore.FieldValue.serverTimestamp(),
        }, { merge: true });

      console.log('✅ Chat room updated successfully');

      // Add message to subcollection
      console.log('📨 Adding message to subcollection...');
      const docRef = await firestore
        .collection('Chats')
        .doc(chatId)
        .collection('messages')
        .add(message);

      console.log('✅ Message added successfully with ID:', docRef);

      setPendingMessages((prev) =>
        prev.map((pendingMessage) =>
          pendingMessage.localId === localId
            ? { ...pendingMessage, id: docRef.id, status: 'sent' }
            : pendingMessage
        )
      );

      // Scroll to bottom
      setTimeout(() => {
        if (flatListRef.current) {
          flatListRef.current.scrollTop = flatListRef.current.scrollHeight;
          console.log('📜 Scrolled to bottom after sending message');
        }
      }, 100);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setPendingMessages((prev) =>
        prev.map((pendingMessage) =>
          pendingMessage.localId === localId
            ? {
                ...pendingMessage,
                status: 'failed',
                errorMessage: 'Failed to send message. Tap to retry.',
              }
            : pendingMessage
        )
      );
      setSendError('Unable to send your message. Please check your connection and try again.');
    } finally {
      console.log('🏁 Message sending process completed');
    }
  }, [input, chatId, currentUserId, otherUserName]);

  useEffect(() => {
    if (!messages.length) {
      return;
    }

    setPendingMessages((prev) =>
      prev.filter((pendingMessage) => {
        if (pendingMessage.status !== 'sent') {
          return true;
        }
        return !messages.some((message) => message.id === pendingMessage.id);
      })
    );
  }, [messages]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      console.log('⌨️ Enter key pressed, sending message');
      e.preventDefault();
      sendMessage();
    }
  };

  const getTimestampValue = (timestamp: any, optimisticTimestamp?: Date) => {
    const source = optimisticTimestamp || timestamp;
    if (!source) return 0;
    if (typeof source === 'number') return source;
    if (source instanceof Date) return source.getTime();
    if (typeof source.toMillis === 'function') return source.toMillis();
    if (typeof source.toDate === 'function') return source.toDate().getTime();
    const parsed = new Date(source).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatTime = (timestamp: any, optimisticTimestamp?: Date) => {
    const finalTimestamp = optimisticTimestamp || timestamp;
    if (!finalTimestamp) return '';
    const date =
      finalTimestamp instanceof Date
        ? finalTimestamp
        : finalTimestamp.toDate
        ? finalTimestamp.toDate()
        : new Date(finalTimestamp);
    if (!timestamp) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (message: EnhancedChatMessage | ChatMessage) => message.senderId === currentUserId;

  const combinedMessages = useMemo(() => {
    const existingIds = new Set(messages.map((message) => message.id));
    const pendingToShow = pendingMessages.filter((pendingMessage) => !existingIds.has(pendingMessage.id));
    return [...messages, ...pendingToShow].sort((a, b) =>
      getTimestampValue(a.timestamp, (a as EnhancedChatMessage).optimisticTimestamp) -
      getTimestampValue(b.timestamp, (b as EnhancedChatMessage).optimisticTimestamp)
    );
  }, [messages, pendingMessages]);

  console.log('🎨 Rendering Chat component:', {
    messagesCount: messages.length,
    hasInput: !!input.trim(),
    isOwnMessage: messages.length > 0 ? isOwnMessage(messages[0]) : null
  });

  if (!currentUserId) {
    console.log('❌ No current user, showing login message');
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Please log in to access chat</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white'
    }}>
      {/* Chat Container */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'white'
      }}>
        {/* Header */}
        <Box sx={{
          height: '60px',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          gap: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <IconButton
            onClick={() => window.history.back()}
            sx={{ color: 'black' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Avatar
            src={otherUserAvatarUrl}
            sx={{
              width: 40,
              height: 40,
              border: '2px solid #e0e0e0'
            }}
          >
            {!otherUserAvatarUrl && (otherUserDisplayName || 'A').charAt(0).toUpperCase()}
          </Avatar>
          <Typography sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'black'
          }}>
            {otherUserDisplayName || 'ifinds user'}
          </Typography>
        </Box>

        {/* Messages Container */}
        <Box
          ref={flatListRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: 'white',
            px: 2,
            py: 1
          }}
        >
              {combinedMessages.length === 0 ? (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography color="text.secondary">
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {/* Date separator */}
              {/* <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Chip 
                  label="March 3" 
                  sx={{ 
                    backgroundColor: '#f0f0f0',
                    color: '#666',
                    fontSize: '12px',
                    height: '24px'
                  }} 
                />
              </Box>
               */}
              {combinedMessages.map((message) => {
                const enhancedMessage = message as EnhancedChatMessage;
                const messageKey = enhancedMessage.localId || enhancedMessage.id;
                const messageStatus = enhancedMessage.status;
                const isPending = messageStatus === 'pending';
                const isFailed = messageStatus === 'failed';

                return (
                <Box
                  key={messageKey}
                  sx={{
                    display: 'flex',
                    justifyContent: isOwnMessage(message) ? 'flex-end' : 'flex-start',
                    mb: 1
                  }}
                >
                  <Box sx={{
                    maxWidth: '70%',
                    position: 'relative'
                  }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        backgroundColor: isOwnMessage(message) ? '#E7FECC' : 'white',
                        color: isOwnMessage(message) ? 'black' : 'black',
                        borderRadius: '18px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        border: isOwnMessage(message) ? '0,5 solid #9BA9B0' : '1px solid #e0e0e0',
                        opacity: isFailed ? 0.6 : 1,
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        justifyContent: 'space-between'
                      }}>
                        <Typography sx={{
                          fontSize: '16px',
                          lineHeight: 1.4,
                          flex: 1
                        }}>
                          {message.text}
                        </Typography>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexShrink: 0
                        }}>
                          <Typography sx={{
                            fontSize: '12px',
                            color: '#999'
                          }}>
                            {isPending ? 'Sending…' : formatTime(message.timestamp, enhancedMessage.optimisticTimestamp)}
                          </Typography>
                          {/* Read receipts for own messages */}
                          {isOwnMessage(message) && !isPending && !isFailed && (
                            <Box sx={{ display: 'flex' }}>
                              <CheckIcon sx={{ fontSize: '14px', color: '#4CAF50' }} />
                              <CheckIcon sx={{ fontSize: '14px', color: '#4CAF50' }} />
                            </Box>
                          )}
                          {isFailed && (
                            <Typography sx={{ fontSize: '12px', color: '#D32F2F', ml: 0.5 }}>
                              Failed
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                    {isFailed && (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#D32F2F',
                          mt: 0.5,
                          ml: isOwnMessage(message) ? 'auto' : 0,
                          textAlign: isOwnMessage(message) ? 'right' : 'left',
                        }}
                      >
                        {enhancedMessage.errorMessage || 'Message failed to send.'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )})}
            </Stack>
          )}
        </Box>

        {/* Input Area */}
        <Box sx={{
          p: 2,
          backgroundColor: 'white',
          borderTop: '1px solid #e0e0e0'
        }}>
          {sendError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setSendError(null)}>
              {sendError}
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  backgroundColor: '#f8f8f8',
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
            <IconButton
              onClick={sendMessage}
              disabled={!input.trim()}
              sx={{
                backgroundColor: '#007AFF',
                color: 'white',
                width: '40px',
                height: '40px',
                '&:hover': {
                  backgroundColor: '#0056CC',
                },
                '&:disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#999',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Chat;